import { useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "@/i18n/LocaleLink";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useContent } from "@/data/content";
import { useLang } from "@/i18n/LanguageContext";

const linkCls = ({ isActive }) =>
  `text-sm tracking-wide transition-colors duration-300 ${isActive ? "text-clay" : "text-ink/80 hover:text-clay"}`;

const Dropdown = ({ label, items, base }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };
  const cancelClose = () => clearTimeout(closeTimer.current);
  return (
    <div
      className="relative"
      onMouseEnter={cancelClose}
      onMouseLeave={scheduleClose}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}
    >
      <button
        className={`flex items-center gap-1 text-sm tracking-wide transition-colors duration-300 py-2 ${open ? "text-clay" : "text-ink/80 hover:text-clay"}`}
        data-testid={`nav-dropdown-${base}`}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); if (e.key === "ArrowDown") setOpen(true); }}
        onFocus={cancelClose}
      >
        {label}
      </button>
      <div className={`absolute left-0 top-full pt-2 transition-all duration-300 z-50 ${open ? "opacity-100 visible translate-y-0" : "opacity-0 invisible translate-y-2"}`}>
        <div className="bg-cream border border-ink/10 shadow-[0_20px_50px_-20px_rgba(44,42,38,0.25)] py-3 min-w-[260px]">
          {items.map((it) => (
            <Link key={it.slug || it.to} to={it.to || `/${base}/${it.slug}`} data-testid={`nav-link-${it.slug || "womens-circle"}`} onClick={() => setOpen(false)}
              className={`block px-6 py-2.5 text-sm outline-none transition-colors duration-200 ${
                it.quiet
                  ? "mt-2 border-t border-ink/10 pt-3.5 text-ink/70 hover:text-clay hover:bg-parchment/60 focus:text-clay focus:bg-parchment/60"
                  : "text-ink/80 hover:text-clay hover:bg-parchment/60 focus:text-clay focus:bg-parchment/60"
              }`}>
              {it.invite && <span className="mb-0.5 block font-serif italic text-xs text-soft/70">{it.invite}</span>}
              {it.name}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

const LangToggle = ({ light = false }) => {
  const { lang, setLang } = useLang();
  const btn = (code) =>
    `transition-colors duration-300 ${lang === code ? "text-clay font-medium" : "text-ink/45 hover:text-ink"}`;
  return (
    <div className="flex items-center gap-1.5 text-xs tracking-[0.2em]" data-testid="lang-toggle">
      <button onClick={() => setLang("nl")} className={btn("nl")} data-testid="lang-toggle-nl">NL</button>
      <span className="text-ink/25">/</span>
      <button onClick={() => setLang("en")} className={btn("en")} data-testid="lang-toggle-en">EN</button>
    </div>
  );
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { ui, thresholds, offerings } = useContent();

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-40 bg-cream/90 backdrop-blur-md border-b border-ink/10" data-testid="navbar">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5" aria-label="Women's Den — home">
          <img src="/logo-symbol-web.png" alt="" className="h-9 lg:h-10 w-auto" />
          <span className="font-serif text-xl lg:text-2xl tracking-tight text-ink whitespace-nowrap">Women's <span className="italic text-clay">Den</span></span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9" data-testid="nav-desktop">
          <Dropdown label={ui.nav.thresholds} items={thresholds} base="thresholds" />
          <Dropdown label={ui.nav.offerings} items={[...offerings, { name: ui.nav.circle, to: "/womens-circle", slug: "womens-circle" }, { name: ui.nav.ritual, invite: ui.nav.ritualInvite, to: "/offerings/ritual-consultation", slug: "ritual-consultation", quiet: true }]} base="offerings" />
          <NavLink to="/about" className={linkCls} data-testid="nav-link-about">{ui.nav.about}</NavLink>
          <NavLink to="/journal" className={linkCls} data-testid="nav-link-journal">{ui.nav.journal}</NavLink>
          <NavLink to="/contact" className={linkCls} data-testid="nav-link-contact">{ui.nav.contact}</NavLink>
          <LangToggle />
          <button
            onClick={() => navigate("/contact")}
            data-testid="nav-book-button"
            className="group ml-2 inline-flex items-center gap-2 rounded-full bg-clay px-6 py-2.5 text-sm text-cream transition-colors duration-300 hover:bg-rust"
          >
            {ui.nav.book}
            <ArrowUpRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </nav>

        <div className="flex items-center gap-5 lg:hidden">
          <LangToggle />
          <button className="text-ink" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="nav-menu-open">
            <Menu size={26} strokeWidth={1.5} />
          </button>
        </div>
      </div>
      </header>

      {/* Rendered outside the header on purpose: the header's backdrop-blur creates a
          containing block that would trap a position:fixed descendant inside the 80px bar. */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 bg-cream flex flex-col w-screen h-[100dvh]"
            data-testid="nav-mobile-menu"
          >
            <div className="h-20 px-6 flex items-center justify-between border-b border-ink/10">
              <span className="flex items-center gap-2.5">
                <img src="/logo-symbol-web.png" alt="" className="h-9 w-auto" />
                <span className="font-serif text-xl">Women's <span className="italic text-clay">Den</span></span>
              </span>
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="nav-menu-close">
                <X size={26} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-6 py-10 flex flex-col gap-1" data-lenis-prevent>
              <p className="text-xs tracking-[0.3em] uppercase text-soft mb-3">{ui.nav.thresholds}</p>
              {thresholds.map((t) => (
                <Link key={t.slug} to={`/thresholds/${t.slug}`} onClick={() => setOpen(false)} data-testid={`nav-mobile-${t.slug}`}
                  className="font-serif text-2xl py-2 text-ink hover:text-clay transition-colors">{t.name}</Link>
              ))}
              <p className="text-xs tracking-[0.3em] uppercase text-soft mt-8 mb-3">{ui.nav.offerings}</p>
              {offerings.map((o) => (
                <Link key={o.slug} to={`/offerings/${o.slug}`} onClick={() => setOpen(false)} data-testid={`nav-mobile-${o.slug}`}
                  className="font-serif text-2xl py-2 text-ink hover:text-clay transition-colors">{o.name}</Link>
              ))}
              <Link to="/womens-circle" onClick={() => setOpen(false)} data-testid="nav-mobile-womens-circle"
                className="font-serif text-2xl py-2 text-ink hover:text-clay transition-colors">{ui.nav.circle}</Link>
              <Link to="/offerings/ritual-consultation" onClick={() => setOpen(false)} data-testid="nav-mobile-ritual-consultation"
                className="font-serif text-2xl py-2 text-ink hover:text-clay transition-colors">
                <span className="block text-sm italic text-soft/70">{ui.nav.ritualInvite}</span>
                {ui.nav.ritual}
              </Link>
              <div className="mt-8 flex flex-col gap-2">
                {[[ui.nav.about, "/about", "about"], [ui.nav.journal, "/journal", "journal"], [ui.nav.contact, "/contact", "contact"]].map(([l, to, id]) => (
                  <Link key={to} to={to} onClick={() => setOpen(false)} data-testid={`nav-mobile-${id}`}
                    className="font-serif text-2xl py-2 text-ink hover:text-clay transition-colors">{l}</Link>
                ))}
              </div>
              <button
                onClick={() => { setOpen(false); navigate("/contact"); }}
                data-testid="nav-mobile-book-button"
                className="mt-10 self-start rounded-full bg-clay px-8 py-3 text-cream text-sm hover:bg-rust transition-colors"
              >
                {ui.nav.book}
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
